import { ValidationResult } from '../../../../../../ui/gzForm/formField/types/form-validator';
import { translateFn } from '../../../../../../utils/translator.helper';
import { HostnameValidator } from '../../../../../../ui/gzForm/validators/hostname.validator';
import { wildcardWithAny } from './wildcard.validators';

// see pathWindowsAbsolute
function pathWindowsAbsolute(value: string): boolean {
  return !!value.match(/^([a-zA-Z]:)(\\[^ \\\/:*?""<>|]+([ ]+[^ \\\/:*?""<>|]+)*)*\\?$/);
}

function pathWindowsFileSpecial(value: string): boolean {
  // check if the value is a non-extendable path
  const nonExtendablePaths = ['All Drives', 'Local Drives', 'Removable Drives'];
  if (nonExtendablePaths.includes(value)) {
    return true;
  }

  // check if the value is a dynamic path
  const dynamicPaths = [
    '%ALLUSERSPROFILE%',
    '%AppData%',
    '%LOCALAPPDATA%',
    '%ProgramFiles%',
    '%USERPROFILE%',
    '%CommonProgramFiles%',
    '%COMMONPROGRAMFILES\\(X86\\)%',
    '%WinDir%',
    '%PROGRAMFILES\\(X86\\)%',
    '%TEMP%',
    '%SystemDrive%',
    '%system%',
  ];
  const matcher = new RegExp('^(' + dynamicPaths.join('|') + ')', 'ig');
  const matches = value.match(matcher);

  // no matches found
  if (!matches) {
    return false;
  }

  // check if the value is exactly a dynamic path
  // (not a dynamic path followed by /folder/file, for example)
  if (value.length === matches[0].length) {
    // since there paths are clearly folders and we have to validate files return false
    return false;
  }

  // by now we know we have a dynamic path followed by a relative one
  const relativePath = value.substring(matches[0].length);
  if (pathWindowsFileAbsolute('C:' + relativePath)) {
    return true;
  }

  // failed to match any successful conditions
  return false;
}

function pathWindowsSpecial(value: string, nonExtendableCustomPaths?: string[]): boolean {
  // check if the value is a non-extendable path
  const nonExtendablePaths = ['All Drives', 'Local Drives', 'Removable Drives'];
  if (nonExtendableCustomPaths) {
    nonExtendablePaths.push(...nonExtendableCustomPaths);
  }

  if (nonExtendablePaths.includes(value)) {
    return true;
  }

  // check if the value is a dynamic path
  const dynamicPaths = [
    '%ALLUSERSPROFILE%',
    '%AppData%',
    '%LOCALAPPDATA%',
    '%ProgramFiles%',
    '%USERPROFILE%',
    '%CommonProgramFiles%',
    '%COMMONPROGRAMFILES\\(X86\\)%',
    '%WinDir%',
    '%PROGRAMFILES\\(X86\\)%',
    '%TEMP%',
    '%SystemDrive%',
    '%system%',
  ];
  const matcher = new RegExp('^(' + dynamicPaths.join('|') + ')', 'ig');
  const matches = value.match(matcher);

  // no matches found
  if (!matches) {
    return false;
  }

  // check if the value is exactly a dynamic path
  // (not a dynamic path followed by /folder/file, for example)
  if (value.length === matches[0].length) {
    return true;
  }

  // by now we know we have a dynamic path followed by a relative one
  const relativePath = value.substring(matches[0].length);
  if (pathWindowsAbsolute('C:' + relativePath)) {
    return true;
  }

  // failed to match any successful conditions
  return false;
}

function isUnixAbsolutePath(value: string): boolean {
  if (!value[0] || value[0] !== '/') {
    return false;
  }

  const nullCharRegex = new RegExp('\0');
  return !nullCharRegex.test(value);
}

function pathUnixFileSpecial(value: string): boolean {
  // check for folder
  if (value.substring(0, 1) === '~') {
    // get the rest of the file path
    const path = value.substring(1);
    if (path === '/' || path === '') {
      return false;
    }

    if (isUnixAbsolutePath(path)) {
      return true;
    }
  }

  // check if the val is in the standard path
  const unixEnvironmentVariables = ['$HOME'];
  for (const unixEnvironmentVariable of unixEnvironmentVariables) {
    if (value === unixEnvironmentVariable) {
      continue;
    }

    // get the rest of the path
    const relativeFilePath = value.substring(unixEnvironmentVariable.length);
    if (relativeFilePath === '/' || relativeFilePath === '') {
      continue;
    }

    if (isUnixAbsolutePath(relativeFilePath)) {
      return true;
    }
  }

  return false;
}

function pathUnixSpecial(value: string): boolean {
  return pathUnixFileSpecial(value);
}

function pathUnixFileAbsolute(value: string): boolean {
  if (!pathUnixAbsolute(value)) {
    return false;
  }

  let otherCharsThanSlashFound = false;
  for (let i = 1; i < value.length; i++) {
    if (value[i] !== '/') {
      otherCharsThanSlashFound = true;
    }
  }

  // must have other chars than slash and the last char must not be a slash
  return otherCharsThanSlashFound && value[value.length - 1] !== '/';
}

function pathUnixAbsolute(value: string): boolean {
  const nullCharRegex = new RegExp('\0');
  if (nullCharRegex.test(value)) {
    return false;
  }

  if (!value[0] || value[0] !== '/') {
    return false;
  }

  return true;
}

// see pathNetwork from ExtJs
export async function pathNetwork(value: string): Promise<boolean> {
  if (value.substring(0, 2) !== '\\\\') {
    return false;
  }

  const newValue = value.substring(2), // without '\\'
    hostnameDelimiterPosition = newValue.indexOf('\\');

  const hostname = hostnameDelimiterPosition !== -1 ? newValue.substring(0, hostnameDelimiterPosition) : newValue;
  if (!hostname) {
    return false;
  }

  const hostnameValidationResult: ValidationResult = await HostnameValidator('', false)(hostname);
  if (!hostnameValidationResult.valid) {
    return false;
  }

  if (hostnameDelimiterPosition !== -1) {
    const sharePath = newValue.substring(hostname.length);
    if (!pathWindowsAbsolute('C:' + sharePath)) {
      return false;
    }
  }

  return true;
}

function pathWindowsNTDevicePath(value: string): boolean {
  return !!value.match(/^\\Device\\.+/i);
}

function pathWindowsFileAbsolute(value: string): boolean {
  return !!value.match(/^([a-zA-Z]:)(\\[^ \\\/:*?""<>|]+([ ]+[^ \\\/:*?""<>|]+)*)+$/);
}

// see universalAbsoluteFilePath from ExtJs
export async function universalAbsoluteFilePath(value: string): Promise<ValidationResult> {
  if (!value) {
    return { valid: true };
  }

  if (
    pathWindowsFileAbsolute(value) ||
    pathWindowsFileSpecial(value) ||
    pathUnixFileSpecial(value) ||
    pathUnixFileAbsolute(value) ||
    (await pathNetwork(value)) ||
    pathWindowsNTDevicePath(value)
  ) {
    return { valid: true };
  }

  return { valid: false, error: translateFn('Invalid path') };
}

// see universalAbsoluteFilePath from ExtJs
export async function universalAbsolutePath(value: string, nonExtendableCustomPaths?: string[]): Promise<ValidationResult> {
  if (!value) {
    return { valid: true };
  }

  if (
    pathWindowsAbsolute(value) ||
    pathWindowsSpecial(value, nonExtendableCustomPaths) ||
    pathUnixSpecial(value) ||
    pathUnixAbsolute(value) ||
    (await pathNetwork(value)) ||
    pathWindowsNTDevicePath(value)
  ) {
    return { valid: true };
  }

  return { valid: false, error: translateFn('Invalid path') };
}

export function exclusionAbsoluteFilePathWithWildcards(value: string): Promise<ValidationResult> | ValidationResult {
  // validate that the given path is a valid path with wildcards
  if (!wildcardWithAny(value, '').valid) {
    return { valid: false, error: translateFn('Invalid path') };
  }

  // temporary render the path with dummy placeholders in order to obtain a static path
  // when the path has wildcards, then replace each wildcard with a dummy placeholder in order to obtain a valid
  let path = value;
  if (path.startsWith('**')) {
    if (path[2] === '\\') {
      path = path.replace('**', 'C:');
    } else if (path[2] === '/') {
      path = path.replace('**', '/root');
    }
  }

  while (path.includes('*') || path.includes('?')) {
    path = path.replace('*', 'ExampleRenderedPath');
    path = path.replace('?', 'A');
  }

  // validate the static file path
  return universalAbsoluteFilePath(path);
}

export function exclusionAbsolutePathWithWildcards(value: string): Promise<ValidationResult> | ValidationResult {
  // validate that the given path is a valid path with wildcards
  if (!wildcardWithAny(value, '').valid) {
    return { valid: false, error: translateFn('Invalid path') };
  }

  // temporary render the path with dummy placeholders in order to obtain a static path
  // when the path has wildcards, then replace each wildcard with a dummy placeholder in order to obtain a valid
  let path = value;
  if (path.startsWith('**')) {
    if (path[2] === '\\') {
      path = path.replace('**', 'C:');
    } else if (path[2] === '/') {
      path = path.replace('**', '/root');
    }
  }

  while (path.includes('*') || path.includes('?')) {
    path = path.replace('*', 'ExampleRenderedPath');
    path = path.replace('?', 'A');
  }

  // validate the static file path
  return universalAbsolutePath(path);
}
